import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ChartBarIcon, 
  UserGroupIcon, 
  AcademicCapIcon, 
  ClipboardDocumentCheckIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  FunnelIcon,
  EyeIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { H1, H2, H3, Body, Card, Badge, Button, Spinner } from '../ui';
import { Input, Select } from '../forms';
import { StatisticsCard } from '../ui/StatisticsCard';
import { useOverallAssessmentSummary, useAssessments, useChildren, useLearningAreas } from '../../hooks/api';
import { AssessmentFilters } from '../../types/api';
import { formatDate } from '../../utils/date';

interface AssessmentDashboardProps {
  onCreateAssessment: () => void;
  onViewAssessment: (id: string) => void;
  canCreateAssessment: boolean;
}

const AssessmentDashboard: React.FC<AssessmentDashboardProps> = ({
  onCreateAssessment,
  onViewAssessment,
  canCreateAssessment,
}) => {
  const [filters, setFilters] = useState<AssessmentFilters>({
    limit: 10,
    skip: 0,
  });
  
  const [selectedTimeframe, setSelectedTimeframe] = useState<'week' | 'month' | 'term'>('month');
  
  const { data: assessmentSummary, isLoading: summaryLoading } = useOverallAssessmentSummary();
  const { data: assessmentsData, isLoading: assessmentsLoading } = useAssessments(filters);
  const { data: childrenData } = useChildren();
  const { data: learningAreas } = useLearningAreas();
  
  const children = childrenData?.items || [];
  const assessments = assessmentsData?.items || [];
  
  // Calculate statistics
  const totalAssessments = assessmentSummary?.total_assessments || 0;
  const publishedAssessments = assessmentSummary?.published_assessments || 0;
  const totalChildren = children.length;
  const assessedChildren = assessmentSummary?.children_assessed || 0;
  const totalLearningAreas = learningAreas?.length || 0;
  
  const publishedPercentage = totalAssessments > 0 ? (publishedAssessments / totalAssessments) * 100 : 0;
  const assessedPercentage = totalChildren > 0 ? (assessedChildren / totalChildren) * 100 : 0;
  
  // Get recent assessments
  const recentAssessments = assessments.slice(0, 5);
  
  // Get learning area progress
  const learningAreaProgress = assessmentSummary?.learning_areas || [];
  
  const getScoreDisplay = (assessment: any) => {
    if (!assessment.score) return 'Not scored';
    
    const scale = assessment.assessment_scale;
    if (!scale) return assessment.score.toString();
    
    let display = assessment.score.toString();
    
    if (scale.min_value !== undefined && scale.max_value !== undefined) {
      display += ` / ${scale.max_value}`;
    }
    
    return display;
  };
  
  const getScoreVariant = (assessment: any) => {
    if (!assessment.score || !assessment.assessment_scale) return 'default';
    
    const scale = assessment.assessment_scale;
    if (!scale.min_value || !scale.max_value) return 'default';
    
    const score = assessment.score;
    const range = scale.max_value - scale.min_value;
    const percentage = ((score - scale.min_value) / range) * 100;
    
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'info';
    if (percentage >= 40) return 'warning';
    return 'danger';
  };

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <H1 className="mb-2">Assessment Dashboard</H1>
          <Body className="text-gray-600 dark:text-gray-400">
            Monitor child progress and assessment completion across all learning areas.
          </Body>
        </div>
        {canCreateAssessment && (
          <Button variant="primary" onClick={onCreateAssessment} className="self-start sm:self-auto">
            <ClipboardDocumentCheckIcon className="w-4 h-4 mr-2" />
            New Assessment
          </Button>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatisticsCard
          title="Total Assessments"
          value={totalAssessments}
          icon={ClipboardDocumentCheckIcon}
          color="primary"
        />
        <StatisticsCard
          title="Published Assessments"
          value={publishedAssessments}
          change={{
            value: publishedPercentage,
            type: publishedPercentage >= 80 ? 'increase' : 'neutral',
            period: 'of total'
          }}
          icon={CheckCircleIcon}
          color="success"
        />
        <StatisticsCard
          title="Children Assessed"
          value={assessedChildren}
          change={{
            value: assessedPercentage,
            type: assessedPercentage >= 80 ? 'increase' : 'neutral',
            period: 'of total'
          }}
          icon={UserGroupIcon}
          color="info"
        />
        <StatisticsCard
          title="Learning Areas"
          value={totalLearningAreas}
          icon={AcademicCapIcon}
          color="secondary"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Assessments */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <H2 className="flex items-center">
                <CalendarDaysIcon className="w-5 h-5 mr-2" />
                Recent Assessments
              </H2>
              <Button variant="ghost" size="sm">
                View All
              </Button>
            </div>
            
            {assessmentsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="md" />
              </div>
            ) : recentAssessments.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardDocumentCheckIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <Body className="text-gray-500">No assessments found</Body>
              </div>
            ) : (
              <div className="space-y-3">
                {recentAssessments.map((assessment) => (
                  <motion.div
                    key={assessment.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => onViewAssessment(assessment.id)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Body className="font-medium text-sm">
                            {assessment.child_name || 'Unknown Child'}
                          </Body>
                          <Badge variant="secondary" size="sm">
                            {assessment.learning_area?.code}
                          </Badge>
                        </div>
                        <Body className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {assessment.learning_outcome?.short_description || assessment.learning_outcome?.description}
                        </Body>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Badge
                          variant={getScoreVariant(assessment)}
                          size="sm"
                        >
                          {getScoreDisplay(assessment)}
                        </Badge>
                        <Badge
                          variant={assessment.is_published ? 'success' : 'default'}
                          size="sm"
                        >
                          {assessment.is_published ? 'Published' : 'Draft'}
                        </Badge>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Learning Area Progress */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <H2 className="flex items-center">
                <ArrowTrendingUpIcon className="w-5 h-5 mr-2" />
                Learning Area Progress
              </H2>
              <Select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value as any)}
                options={[
                  { value: 'week', label: 'This Week' },
                  { value: 'month', label: 'This Month' },
                  { value: 'term', label: 'This Term' },
                ]}
              />
            </div>
            
            {learningAreaProgress.length === 0 ? (
              <div className="text-center py-8">
                <AcademicCapIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <Body className="text-gray-500">No learning area data available</Body>
              </div>
            ) : (
              <div className="space-y-4">
                {learningAreaProgress.map((area) => (
                  <motion.div
                    key={area.learning_area.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Badge variant="info" size="sm">
                          {area.learning_area.code}
                        </Badge>
                        <Body className="font-medium text-sm">
                          {area.learning_area.name}
                        </Body>
                      </div>
                      <Body className="text-xs text-gray-500">
                        {area.published_assessments} / {area.total_assessments} published
                      </Body>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Progress</span>
                        <span className="font-medium">
                          {area.total_assessments > 0 
                            ? Math.round((area.published_assessments / area.total_assessments) * 100)
                            : 0
                          }%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${area.total_assessments > 0 
                              ? (area.published_assessments / area.total_assessments) * 100 
                              : 0}%` 
                          }}
                        />
                      </div>
                    </div>
                    
                    {area.latest_assessment_date && (
                      <Body className="text-xs text-gray-500 mt-2">
                        Last assessed: {formatDate(area.latest_assessment_date)}
                      </Body>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <div className="p-6">
          <H2 className="mb-4">Quick Actions</H2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border-2 border-primary-100 dark:border-primary-800 hover:border-primary-200 dark:hover:border-primary-700 transition-colors cursor-pointer"
              onClick={onCreateAssessment}
            >
              <div className="flex items-center space-x-3">
                <ClipboardDocumentCheckIcon className="w-8 h-8 text-primary-600" />
                <div>
                  <H3 className="text-primary-900 dark:text-primary-200">Create Assessment</H3>
                  <Body className="text-sm text-primary-700 dark:text-primary-300">
                    Add a new assessment for a child
                  </Body>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-100 dark:border-green-800 hover:border-green-200 dark:hover:border-green-700 transition-colors cursor-pointer"
            >
              <div className="flex items-center space-x-3">
                <ChartBarIcon className="w-8 h-8 text-green-600" />
                <div>
                  <H3 className="text-green-900 dark:text-green-200">View Reports</H3>
                  <Body className="text-sm text-green-700 dark:text-green-300">
                    Generate assessment reports
                  </Body>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-2 border-blue-100 dark:border-blue-800 hover:border-blue-200 dark:hover:border-blue-700 transition-colors cursor-pointer"
            >
              <div className="flex items-center space-x-3">
                <UserGroupIcon className="w-8 h-8 text-blue-600" />
                <div>
                  <H3 className="text-blue-900 dark:text-blue-200">Bulk Actions</H3>
                  <Body className="text-sm text-blue-700 dark:text-blue-300">
                    Manage multiple assessments
                  </Body>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AssessmentDashboard;
